import type { Result } from 'neverthrow';

import { err, ok } from 'neverthrow';

import type { DirectoryHandleAlternative } from './opfs-alternative';

import { OPFSAlternative } from './opfs-alternative';

export type Prettify<T> = {
    [K in keyof T]: T[K];
} & {};

/**
 * Represents a basic nested object structure.
 * Using 'any' for maximum flexibility, but you might constrain
 * this further in a specific application.
 */
type NestedObject = Record<string, any>;

/**
 * Sets a value within a nested object structure based on a flattened key path.
 * Modifies the target object in place or creates a new one if none is provided.
 * Throws an error for invalid paths or attempts at prototype pollution.
 *
 * @template T - The type of the target object, defaulting to NestedObject.
 * @param {string} path - The flattened key path using dot notation (e.g., "user.address.street").
 * @param {any} value - The value to set at the specified path.
 * @param {T} [target] - The target object to modify. If not provided, a new empty object is created.
 * @returns {T} The modified (or newly created) target object.
 * @throws {TypeError} If the path is not a non-empty string.
 * @throws {Error} If a path segment attempts prototype pollution (e.g., "__proto__", "constructor", "prototype").
 * @throws {TypeError} If attempting to set a property on a non-object value along the path (unless it's undefined or null).
 *
 * @example
 * const obj = {};
 * setValueByPath('user.name', 'John Doe', obj);
 * // obj is now { user: { name: 'John Doe' } }
 *
 * @example
 * const obj2 = { user: { age: 30 } };
 * setValueByPath('user.address.city', 'New York', obj2);
 * // obj2 is now { user: { age: 30, address: { city: 'New York' } } }
 *
 * @example
 * const newObj = setValueByPath('a.b.c', 123);
 * // newObj is { a: { b: { c: 123 } } }
 *
 * @example
 * const obj3 = { a: 5 };
 * // This will throw a TypeError because 'a' is not an object:
 * // setValueByPath('a.b', 'Error', obj3);
 */
export function setValueByPath<T extends NestedObject = NestedObject>(
    path: string,
    value: any,
    target: T = {} as T,
): Result<T, Error> {
    if (typeof path !== 'string' || path.trim() === '') {
        return err(new TypeError('Path must be a non-empty string.'));
    }

    const keys = path.split('.');
    let currentLevel: any = target; // Use 'any' for traversal flexibility

    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];

        // Prevent prototype pollution
        if (
            key === '__proto__'
            || key === 'constructor'
            || key === 'prototype'
        ) {
            return err(new Error(`Invalid path segment detected: "${key}"`));
        }

        // If the key doesn't exist, create an empty object.
        if (currentLevel[key] === undefined || currentLevel[key] === null) {
            currentLevel[key] = {};
        }
        // If the key exists but isn't an object, throw an error.
        else if (typeof currentLevel[key] !== 'object') {
            const currentPath = keys.slice(0, i + 1).join('.');
            return err(new TypeError(
                `Cannot set property "${keys[i + 1]}" on non-object value at path "${currentPath}" (value is ${typeof currentLevel[key]}).`,
            ));
        }

        // Move down to the next level
        currentLevel = currentLevel[key];
    }

    const lastKey = keys[keys.length - 1];

    // Final check for prototype pollution on the last key
    if (
        lastKey === '__proto__'
        || lastKey === 'constructor'
        || lastKey === 'prototype'
    ) {
        return err(new Error(`Invalid path segment detected: "${lastKey}"`));
    }

    // Set the final value
    currentLevel[lastKey] = value;

    return ok(target);
}

/**
 * Checks if a value is a plain object (e.g., created via {} or new Object()).
 * Excludes arrays, null, and other object-like types.
 * @param item - The value to check.
 * @returns True if the item is a plain object, false otherwise.
 */
function isPlainObject(item: unknown): item is Record<string, unknown> {
    return (
        item !== null && typeof item === 'object' && item.constructor === Object
    );
}

/**
 * Deeply merges two objects. Creates a new object containing properties from
 * both `target` and `source`.
 *
 * - If a key exists in both objects and both values are plain objects,
 *   it recursively merges them.
 * - Otherwise, the value from the `source` object overwrites the value
 *   from the `target` object for that key.
 * - Properties unique to either object are included.
 * - Arrays are treated like primitive values (the source array replaces the target array).
 * - Does not modify the original `target` or `source` objects.
 *
 * @template T - The type of the target object.
 * @template S - The type of the source object.
 * @param target - The base object.
 * @param source - The object with properties to merge into the target.
 * @returns A new object representing the deep merge of target and source.
 *          The return type is inferred but can be complex; often `any` or
 *          `Record<string, any>` is practical for the result. Consider
 *          casting or type guards on the result if needed.
 *
 * @example
 * const defaults = { config: { timeout: 500, retries: 3 }, user: 'admin' };
 * const specifics = { config: { timeout: 1000 }, theme: 'dark' };
 * const merged = deepMerge(defaults, specifics);
 * // merged is:
 * // {
 * //   config: { timeout: 1000, retries: 3 },
 * //   user: 'admin',
 * //   theme: 'dark'
 * // }
 */
export function deepMerge<T extends object, S extends object>(
    target: T,
    source: S,
): Record<string, any> {
    // Start with a shallow clone of the target
    // Or an empty object if target is not suitable
    const output: Record<string, any> = isPlainObject(target)
        ? { ...target }
        : {};

    if (!isPlainObject(source)) {
        // If source isn't an object, there's nothing to merge from it at this level.
        // If target wasn't an object either, output is {}, otherwise it's { ...target }
        return output;
    }

    Object.keys(source).forEach((key) => {
        const targetValue = output[key];
        // Need 'as any' because source is generic S, not necessarily Record<string, any>
        const sourceValue = (source as any)[key];

        if (isPlainObject(targetValue) && isPlainObject(sourceValue)) {
            // Recursively merge if both values are plain objects
            output[key] = deepMerge(targetValue, sourceValue);
        }
        else {
            // Otherwise, source value overwrites target value
            // This handles primitives, arrays, null, functions, etc.
            output[key] = sourceValue;
        }
    });

    return output;
}

/**
 * Converts a string to Pascal case.
 * Handles various input formats including:
 * - camelCase (e.g., "floorNumber" -> "FloorNumber")
 * - snake_case (e.g., "floor_number" -> "FloorNumber")
 * - kebab-case (e.g., "floor-number" -> "FloorNumber")
 * - space separated words (e.g., "floor number" -> "FloorNumber")
 * - existing Pascal case strings (e.g., "FloorNumber" -> "FloorNumber")
 *
 * @param str - The string to convert to Pascal case
 * @returns The string converted to Pascal case
 *
 * @example
 * stringToPascalCase('hello-world') // returns 'HelloWorld'
 * stringToPascalCase('hello_world') // returns 'HelloWorld'
 * stringToPascalCase('helloWorld') // returns 'HelloWorld'
 * stringToPascalCase('hello world') // returns 'HelloWorld'
 * stringToPascalCase('HelloWorld') // returns 'HelloWorld'
 * stringToPascalCase('ElectricalProjectDistributionBoardId') // returns 'ElectricalProjectDistributionBoardId'
 */
export function stringToPascalCase(str: string): string {
    if (!str)
        return '';

    // If the string is already in Pascal case (starts with uppercase and has no separators)
    if (/^[A-Z][a-zA-Z0-9]*$/.test(str)) {
        return str;
    }

    // Handle camelCase by inserting a space before capital letters
    const withSpaces = str.replace(/([A-Z])/g, ' $1').trim();

    return withSpaces
        // Split by common separators (space, underscore, hyphen)
        .split(/[\s_-]+/)
        // Capitalize first letter of each word and lowercase the rest
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        // Join words together
        .join('');
}

/**
 * Converts any value to Pascal case format.
 * - For strings: converts the string to Pascal case
 * - For objects: converts all keys to Pascal case recursively
 * - For arrays: converts all elements to Pascal case recursively
 * - For other values: returns the value as is
 *
 * Note: This function only converts keys to Pascal case, it does not modify values.
 *
 * @param value - The value to convert to Pascal case
 * @returns The converted value
 *
 * @example
 * // String conversion
 * toPascalCase('hello-world') // returns 'HelloWorld'
 *
 * // Object conversion
 * toPascalCase({
 *   user_name: 'John',
 *   contact_info: {
 *     email_address: 'john@example.com'
 *   }
 * })
 * // returns {
 * //   UserName: 'John',
 * //   ContactInfo: {
 * //     EmailAddress: 'john@example.com'
 * //   }
 * // }
 *
 * // Array conversion
 * toPascalCase(['hello-world', { user_name: 'John' }])
 * // returns ['HelloWorld', { UserName: 'John' }]
 *
 * // Mixed conversion
 * toPascalCase({
 *   user_name: 'John',
 *   tags: ['hello-world', 'good-morning'],
 *   settings: {
 *     theme_color: 'dark'
 *   }
 * })
 * // returns {
 * //   UserName: 'John',
 * //   Tags: ['HelloWorld', 'GoodMorning'],
 * //   Settings: {
 * //     ThemeColor: 'dark'
 * //   }
 * // }
 */
export function toPascalCase<T>(value: T): T {
    if (value === null || value === undefined)
        return value;

    if (typeof value === 'string')
        return stringToPascalCase(value) as unknown as T;

    if (Array.isArray(value))
        return value.map(item => toPascalCase(item)) as unknown as T;

    if (typeof value === 'object') {
        const result: Record<string, any> = {};

        for (const [key, val] of Object.entries(value)) {
            const pascalKey = stringToPascalCase(key);
            // Don't convert the value, just pass it through
            result[pascalKey] = val;
        }

        return result as T;
    }

    return value;
}

/**
 * Converts a string to camel case.
 * Handles various input formats including:
 * - Pascal case (e.g., "FloorNumber" -> "floorNumber")
 * - snake_case (e.g., "floor_number" -> "floorNumber")
 * - kebab-case (e.g., "floor-number" -> "floorNumber")
 */
function stringToCamelCase(str: string): string {
    return str[0].toLowerCase() + str.slice(1);
}

/**
 * Converts any value to camel case format.
 * - For strings: converts the string to camel case
 * - For objects: converts all keys to camel case recursively
 * - For arrays: converts all elements to camel case recursively
 * - For other values: returns the value as is
 *
 * @param value - The value to convert to camel case
 * @returns The converted value
 *
 * @example
 * // String conversion
 * toCamelCase('hello-world') // returns 'helloWorld'
 */
export function toCamelCase<T>(value: T): T {
    if (value === null || value === undefined)
        return value;

    if (typeof value === 'string')
        return stringToCamelCase(value) as unknown as T;

    if (Array.isArray(value))
        return value.map(item => toCamelCase(item)) as unknown as T;

    if (typeof value === 'object') {
        const result: Record<string, any> = {};

        for (const [key, val] of Object.entries(value)) {
            const camelKey = stringToCamelCase(key);
            // Don't convert the value, just pass it through
            result[camelKey] = val;
        }

        return result as T;
    }

    return value;
}

/**
 * Creates a debounced version of a function that delays its execution until after
 * a specified time has elapsed since the last time it was called.
 *
 * @param fn The function to debounce
 * @param delay The delay in milliseconds (default: 500ms)
 * @returns A debounced version of the provided function
 *
 * @example
 * const debouncedSearch = debounce((query) => {
 *   // Search logic here
 * }, 300);
 *
 * // Call multiple times but only executes once after 300ms of inactivity
 * debouncedSearch('test');
 */
export function debounce<T extends (...args: any[]) => any>(
    fn: T,
    delay = 500,
): (...args: Parameters<T>) => void {
    let timeoutId: ReturnType<typeof setTimeout>;

    return function (...args: Parameters<T>) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

export function createScopeId(kind: string, identifiers: Record<string, any> = {}) {
    return JSON.stringify([kind?.toLowerCase(), identifiers]);
}

/**
 * Gets the best available File System Access API handle.
 * It prefers the native Origin Private File System (OPFS), but falls back to an
 * IndexedDB-based alternative if the native API is not fully supported
 * (e.g., on some versions of Safari).
 */
export async function getFileSystemRoot(): Promise<FileSystemDirectoryHandle | DirectoryHandleAlternative> {
    try {
        // The 'createWritable' check is a good way to detect incomplete
        // implementations, as you pointed out.
        if (
            navigator.storage
            && typeof navigator.storage.getDirectory === 'function'
            && typeof FileSystemFileHandle.prototype.createWritable === 'function'
        ) {
            console.log('Using native OPFS.');

            const fs = await navigator.storage.getDirectory();

            return fs;
        }
    }
    catch (e) {
        console.error('Error checking for native OPFS, falling back.', e);
    }

    console.warn('Native OPFS not fully supported. Using IndexedDB fallback.');
    const fs = await new OPFSAlternative().getRoot();
    return fs;
}
